import {
  BufferGeometry,
  CatmullRomCurve3,
  Color,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import type { RegionDefinition } from '../types';

interface Flow {
  region: string;
  line: Line;
  particles: Mesh[];
  curve: CatmullRomCurve3;
  enabled: boolean;
}

export class SignalFlowOverlay {
  readonly root = new Group();
  private readonly flows: Flow[] = [];
  private readonly sphere = new SphereGeometry(0.0011, 8, 6);

  constructor(regions: RegionDefinition[], mobile: boolean) {
    for (const region of regions) {
      const center = new Vector3(...region.center);
      const points = [
        center.clone().add(new Vector3(-0.033, 0.012, -0.009)),
        center.clone().add(new Vector3(-0.005, 0.013, -0.009)),
        center.clone().add(new Vector3(0.012, 0.013, 0)),
        center.clone().add(new Vector3(0.036, 0.014, 0)),
      ];
      const curve = new CatmullRomCurve3(points);
      const geometry = new BufferGeometry().setFromPoints(curve.getPoints(36));
      const line = new Line(
        geometry,
        new LineBasicMaterial({ color: region.accent, transparent: true, opacity: 0.16 }),
      );
      this.root.add(line);
      const particles: Mesh[] = [];
      const count = mobile ? 3 : 6;
      for (let i = 0; i < count; i += 1) {
        const particle = new Mesh(
          this.sphere,
          new MeshBasicMaterial({ color: new Color(region.accent), transparent: true, opacity: 0 }),
        );
        particles.push(particle);
        this.root.add(particle);
      }
      this.flows.push({ region: region.id, line, particles, curve, enabled: false });
    }
  }

  setState(region: string, enabled: boolean): void {
    const flow = this.flows.find((item) => item.region === region);
    if (!flow) return;
    flow.enabled = enabled;
    (flow.line.material as LineBasicMaterial).opacity = enabled ? 0.85 : 0.12;
    for (const particle of flow.particles) {
      (particle.material as MeshBasicMaterial).opacity = enabled ? 1 : 0;
    }
  }

  update(time: number): void {
    for (const flow of this.flows) {
      if (!flow.enabled) continue;
      flow.particles.forEach((particle, index) => {
        const phase = (time * 0.23 + index / flow.particles.length) % 1;
        particle.position.copy(flow.curve.getPointAt(phase));
      });
    }
  }
}
